import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../../lib/useApi'

const ALL_CRITERIA = [
  { key: 'wake_ok',          label: 'Wake on time'      },
  { key: 'training_done',    label: 'Training done'     },
  { key: 'cardio_done',      label: 'Cardio done'       },
  { key: 'protein_ok',       label: 'Protein hit'       },
  { key: 'sleep_ok',         label: 'Sleep ≥ 7h'        },
  { key: 'no_phone_morning', label: 'No phone AM'       },
  { key: 'diary_done',       label: 'Diary written'     },
  { key: 'breathing_done',   label: 'Breathing'         },
  { key: 'meditation_done',  label: 'Meditation'        },
  { key: 'abs_done',         label: 'Abs / Core'        },
  { key: 'water_ok',         label: 'Wasser 2L+'        },
  { key: 'cold_shower',      label: 'Kalte Dusche'      },
  { key: 'social_done',      label: 'Soziales'          },
  { key: 'no_alcohol',       label: 'Kein Alkohol'      },
  { key: 'reading_done',     label: 'Lesen'             },
]

const DEFAULT_KEYS = ['wake_ok','training_done','cardio_done','protein_ok','sleep_ok','no_phone_morning','diary_done']
const LS_KEY = (date) => `arise:srank-override:${date}`

export default function SRankSection() {
  const api = useApi()
  const qc = useQueryClient()
  const today = new Date().toISOString().slice(0, 10)
  const { data } = useQuery({ queryKey: ['daily-today'], queryFn: () => api.get('/daily') })
  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: () => api.get('/profile') })

  const userCriteria = profile?.goals?.srank_keys
  const activeKeys = Array.isArray(userCriteria) && userCriteria.length ? userCriteria : DEFAULT_KEYS
  const criteria = ALL_CRITERIA.filter((c) => activeKeys.includes(c.key))

  const values = data?.values || {}
  const [override, setOverride] = useState({})
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    try { setOverride(JSON.parse(localStorage.getItem(LS_KEY(today)) || '{}')) } catch { /* noop */ }
  }, [today])

  const ck = (k) => (k in override) ? !!override[k] : !!values[k]

  function toggle(k) {
    const next = { ...override, [k]: !ck(k) }
    setOverride(next)
    localStorage.setItem(LS_KEY(today), JSON.stringify(next))
    api.put('/daily', { values: { [k]: next[k] } }).then(() => {
      qc.invalidateQueries({ queryKey: ['daily-today'] })
      qc.invalidateQueries({ queryKey: ['daily-range'] })
    }).catch(() => {})
  }

  async function saveKeys(keys) {
    qc.setQueryData(['profile'], (old) => ({
      ...(old || {}),
      goals: { ...((old || {}).goals || {}), srank_keys: keys },
    }))
    api.patch('/profile', { goals: { ...(profile?.goals || {}), srank_keys: keys } }).catch(() => {})
  }

  function togglePick(k) {
    const set = new Set(activeKeys)
    set.has(k) ? set.delete(k) : set.add(k)
    saveKeys([...set])
  }

  const score = criteria.filter((c) => ck(c.key)).length
  const total = criteria.length || 1
  const ratio = score / total
  const rank = ratio === 1 ? 'S'
            : ratio >= 0.85 ? 'A'
            : ratio >= 0.70 ? 'B'
            : ratio >= 0.50 ? 'C'
            : ratio >= 0.30 ? 'D' : 'E'

  return (
    <section className="space-y-3">
      <div className="section-h">S-Rank</div>
      <div className="border border-card bg-card rounded-themed p-4 flex flex-col md:flex-row gap-4">
        <div className="md:w-48 grid place-items-center border border-card rounded-themed py-6">
          <div className="font-display text-7xl glow num">{rank}</div>
          <div className="label-mini mt-2">{score}/{total}</div>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <div className="label-mini">Kriterien heute</div>
            <button onClick={() => setPickerOpen((x) => !x)}
                    className="text-[10px] opacity-60 hover:text-accent">
              {pickerOpen ? 'fertig' : 'anpassen'}
            </button>
          </div>
          {pickerOpen ? (
            <div className="grid grid-cols-2 gap-2">
              {ALL_CRITERIA.map((c) => {
                const on = activeKeys.includes(c.key)
                return (
                  <button key={c.key} onClick={() => togglePick(c.key)}
                          className={`tap text-left px-3 py-2 rounded-themed border text-sm ${
                            on ? 'border-[var(--accent)] bg-[var(--accent)]/10' : 'border-card opacity-60'
                          }`}>
                    {on ? '✓' : '○'} {c.label}
                  </button>
                )
              })}
            </div>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {criteria.map((c) => {
                const on = ck(c.key)
                return (
                  <li key={c.key}>
                    <button onClick={() => toggle(c.key)}
                            className={`tap w-full text-left px-3 py-2 rounded-themed border text-sm ${
                              on ? 'border-[var(--accent)] bg-[var(--accent)]/10' : 'border-card'
                            }`}>
                      <span className="font-display mr-2">{on ? '✓' : '○'}</span>{c.label}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}
